import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Waves, ShieldCheck, ShieldX, Copy, ArrowLeft, KeyRound } from "lucide-react";
import { toast } from "sonner";

const SAMPLE_TOKEN = `{"core_id":"*1SADQZE2QZQ6VNF","email":"test@albatross.com","expires_at":"2026-08-08T04:31:07+00:00","issued_at":"2026-07-25T04:31:07+00:00","license_id":"059d6c94-fe39-414d-aff1-8c3bd8e3a486","mac_address":"00:1A:2B:3C:4D:5E","plugin":"lifelike-multiband-comp","plugin_name":"LifeLike Multiband Comp","type":"trial","signature":"q2hZkxN5k+4H+bR6tV9..."}`;

const LUA_SNIPPET = `-- Offline HMAC-SHA256 license verification in Q-SYS Lua
-- Embed your shared secret in the plugin build (keep the plugin private).
local rapidjson = require("rapidjson")
local LICENSE_SECRET = "<YOUR_SHARED_SECRET>"

local function verify(token_str)
  local obj = rapidjson.decode(token_str)
  local sig = obj.signature
  obj.signature = nil

  -- Canonical JSON: keys sorted alphabetically, MUST match the server which
  -- uses Python json.dumps(payload, sort_keys=True) => ", " and ": " separators.
  local canonical = canonicalize(obj)   -- your sorted-key encoder

  local raw = Crypto.HMAC("sha256", LICENSE_SECRET, canonical)  -- raw bytes
  local expected = Crypto.Base64Encode(raw)                     -- standard base64
  if expected ~= sig then return false, "bad signature" end

  -- Bind to this Core (or accept wildcard)
  if obj.core_id ~= "*" and obj.core_id ~= System.LockingId then
    return false, "core mismatch"
  end

  -- Bind to this NIC MAC (or accept wildcard)
  if obj.mac_address ~= "*" and obj.mac_address ~= get_core_mac() then
    return false, "mac mismatch"
  end

  -- Expiry: absence of expires_at means perpetual
  if obj.expires_at and iso8601_to_epoch(obj.expires_at) < os.time() then
    return false, "expired"
  end

  return true, obj
end`;

export default function Verify() {
  const [token, setToken] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const verify = async () => {
    if (!token.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/verify", { token: token.trim() });
      setResult(data);
    } catch {
      toast.error("Verification request failed");
    } finally {
      setLoading(false);
    }
  };

  const copy = (text, msg) => {
    navigator.clipboard.writeText(text);
    toast.success(msg);
  };

  return (
    <div className="min-h-screen bg-background grid-bg">
      <header className="backdrop-blur-xl bg-zinc-950/80 border-b border-zinc-800/60">
        <div className="max-w-5xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="verify-home-link">
            <div className="h-9 w-9 rounded-sm bg-orange-600 flex items-center justify-center">
              <Waves className="h-5 w-5 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-black tracking-tighter text-lg">LifeLike License Portal</span>
          </Link>
          <Link to="/" data-testid="verify-back-link" className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-zinc-400 hover:text-orange-400 transition-colors">
            <ArrowLeft className="h-4 w-4" /> Portal
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 py-12">
        <p className="font-mono text-xs uppercase tracking-widest text-orange-500 mb-3">HMAC-SHA256 · License verification</p>
        <h1 className="text-4xl sm:text-5xl font-black tracking-tighter">Verify a license</h1>
        <p className="text-zinc-400 mt-3 max-w-2xl">
          Paste a signed license token (a JSON object) to check its signature, expiry and
          revocation online. For offline checks inside a plugin, embed the shared secret in
          your Lua and re-compute the HMAC over the canonical payload.
        </p>

        <div className="grid lg:grid-cols-2 gap-6 mt-10">
          {/* Verifier */}
          <Card className="bg-zinc-950 border-zinc-800 rounded-md p-6">
            <h2 className="text-xl font-bold tracking-tight mb-4">Online verifier</h2>
            <Textarea
              data-testid="verify-token-input"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder='Paste license token JSON, e.g. {"core_id":"...","signature":"..."}'
              className="rounded-sm bg-black border-zinc-800 font-mono text-xs min-h-[160px] break-all"
            />
            <div className="flex gap-2 mt-3">
              <Button
                data-testid="verify-submit-btn"
                onClick={verify}
                disabled={loading}
                className="flex-1 rounded-sm bg-orange-600 hover:bg-orange-700 text-white hover:text-white font-semibold"
              >
                {loading ? "Verifying..." : "Verify token"}
              </Button>
              <Button
                data-testid="verify-sample-btn"
                variant="outline"
                onClick={() => setToken(SAMPLE_TOKEN)}
                className="rounded-sm border-zinc-700 hover:bg-zinc-800 hover:text-white"
              >
                Sample
              </Button>
            </div>

            {result && (
              <div
                data-testid="verify-result"
                className={`mt-4 rounded-md border p-4 ${result.valid ? "border-emerald-500/30 bg-emerald-500/10" : "border-red-500/30 bg-red-500/10"}`}
              >
                <div className="flex items-center gap-2">
                  {result.valid ? (
                    <ShieldCheck className="h-5 w-5 text-emerald-400" />
                  ) : (
                    <ShieldX className="h-5 w-5 text-red-400" />
                  )}
                  <span className={`font-bold ${result.valid ? "text-emerald-400" : "text-red-400"}`}>
                    {result.valid ? "VALID LICENSE" : "INVALID"}
                  </span>
                </div>
                {result.reason && <p className="text-sm text-red-300 mt-1 font-mono">{result.reason}</p>}
                {result.payload && (
                  <pre className="mt-3 bg-black border border-zinc-800 rounded-sm p-3 font-mono text-xs text-zinc-300 overflow-auto">
{JSON.stringify(result.payload, null, 2)}
                  </pre>
                )}
              </div>
            )}
          </Card>

          {/* Token structure + Lua */}
          <div className="space-y-6">
            <Card className="bg-zinc-950 border-zinc-800 rounded-md p-6">
              <div className="flex items-center gap-2 mb-3">
                <KeyRound className="h-5 w-5 text-orange-500" />
                <h2 className="text-xl font-bold tracking-tight">Token structure</h2>
              </div>
              <p className="text-sm text-zinc-400 mb-3">
                A JSON object of the payload fields plus a <span className="font-mono text-orange-400">signature</span>:
                standard Base64 HMAC-SHA256 over <span className="font-mono text-orange-400">json.dumps(payload, sort_keys=True)</span>
                (all fields except <span className="font-mono text-orange-400">signature</span>). Omit
                <span className="font-mono text-orange-400"> expires_at</span> for perpetual. <span className="font-mono text-orange-400">core_id</span> is
                the Core's <span className="font-mono">System.LockingId</span> or <span className="font-mono text-orange-400">"*"</span> wildcard.
              </p>
              <div className="flex items-center justify-between mb-1">
                <div className="font-mono text-xs uppercase tracking-widest text-zinc-500">Example token</div>
                <Button size="sm" variant="outline" className="rounded-sm border-zinc-700 hover:bg-zinc-800 hover:text-white" onClick={() => copy(SAMPLE_TOKEN, "Sample token copied")} data-testid="copy-sample">
                  <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy
                </Button>
              </div>
              <pre className="bg-black border border-zinc-800 rounded-sm p-3 font-mono text-[11px] text-orange-400 break-all whitespace-pre-wrap">
                {SAMPLE_TOKEN}
              </pre>
            </Card>

            <Card className="bg-zinc-950 border-zinc-800 rounded-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold tracking-tight">Lua verification</h2>
                <Button size="sm" variant="outline" className="rounded-sm border-zinc-700 hover:bg-zinc-800 hover:text-white" onClick={() => copy(LUA_SNIPPET, "Lua snippet copied")} data-testid="copy-lua">
                  <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy
                </Button>
              </div>
              <pre className="bg-black border border-zinc-800 rounded-sm p-3 font-mono text-[11px] text-zinc-300 overflow-auto max-h-80">
                {LUA_SNIPPET}
              </pre>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
